import type { Attachment } from '../api/types';
import {
  commentAttachmentIds, readVideoComments, videoCommentFrames, withVideoCommentFrames, MAX_VIDEO_COMMENT_FRAMES, MAX_VIDEO_COMMENTS_JSON_LENGTH,
  type VideoComment, type VideoCommentDraft, type VideoCommentFrame, type VideoFrameDraft,
} from './videoComments';

interface GroupPersistence {
  upload: (file: File) => Promise<Attachment | null>;
  rollback: (files: Attachment[]) => Promise<void>;
  commit: (comment: VideoComment, uploaded: Attachment[]) => void;
}

/** Validate the complete list before changing HTML or draft ownership. The
 * reader has bounds, so a successful save must never silently lose a group. */
export function serializeVideoCommentsForSave(comments: VideoComment[]): string {
  const raw = JSON.stringify(comments);
  if (raw.length > MAX_VIDEO_COMMENTS_JSON_LENGTH) throw new Error('Комментариев к этому видео слишком много. Сохраните их отдельным сообщением; текущие правки остаются в окне.');
  const restored = readVideoComments(raw);
  if (restored.length !== comments.length || comments.some(comment => {
    const saved = restored.find(item => item.id === comment.id);
    if (!saved || saved.text !== comment.text || saved.voiceAttachmentId !== comment.voiceAttachmentId || saved.voiceUrl !== comment.voiceUrl) return true;
    if (Boolean(saved.videoAdjustments) !== Boolean(comment.videoAdjustments) || (['brightness','contrast','hue','saturation'] as const).some(key => saved.videoAdjustments?.[key] !== comment.videoAdjustments?.[key]) || saved.videoAdjustmentsSavedAt !== comment.videoAdjustmentsSavedAt) return true;
    const frames = videoCommentFrames(comment), savedFrames = videoCommentFrames(saved);
    return frames.length !== savedFrames.length || frames.some(frame => !savedFrames.some(item => item.id === frame.id
      && item.time === frame.time && item.frame === frame.frame && item.attachmentId === frame.attachmentId && item.imageUrl === frame.imageUrl));
  })) throw new Error('Не удалось сохранить все комментарии и кадры. Текущие правки остаются в окне.');
  return raw;
}

/** Upload all new images and the shared recording before changing the comment.
 * Obsolete attachments are returned for cleanup only after a successful commit.
 * Originals/borrowed history attachments are never deleted by this helper. */
export async function persistVideoCommentGroup(
  frames: VideoFrameDraft[], draft: VideoCommentDraft, previous: VideoComment | undefined,
  voice: File | null | undefined, actions: GroupPersistence,
) {
  if (!frames.length) throw new Error('Добавьте хотя бы один кадр к комментарию.');
  if (frames.length > MAX_VIDEO_COMMENT_FRAMES) throw new Error('В комментарии слишком много кадров.');
  const oldFrames = previous ? videoCommentFrames(previous) : [];
  const ids = new Set<string>();
  for (const frame of frames) {
    if (!frame.id || ids.has(frame.id) || !Number.isFinite(frame.time) || frame.time < 0) throw new Error('Не удалось сохранить список кадров.');
    ids.add(frame.id);
    if (!frame.file && (!frame.existing || !oldFrames.some(old => old.id === frame.existing!.id
      && old.attachmentId === frame.existing!.attachmentId && old.imageUrl === frame.existing!.imageUrl))) {
      throw new Error('Изображение кадра недоступно. Нарисуйте правку заново.');
    }
  }
  const uploaded: Attachment[] = [];
  try {
    const savedFrames: VideoCommentFrame[] = [];
    for (const frame of frames) {
      if (frame.file) {
        const attachment = await actions.upload(frame.file);
        if (!attachment) throw new Error('Не удалось загрузить кадр. Комментарий пока не сохранён.');
        uploaded.push(attachment);
        savedFrames.push({ id: frame.id, time: frame.time, frame: frame.frame, imageUrl: attachment.attachmentUrl, attachmentId: attachment.id });
      } else {
        savedFrames.push({ ...frame.existing!, id: frame.id, time: frame.time, frame: frame.frame });
      }
    }
    const audio = voice ? await actions.upload(voice) : undefined;
    if (voice && !audio) throw new Error('Не удалось сохранить голос. Комментарий пока не сохранён.');
    if (audio) uploaded.push(audio);
    const saved = withVideoCommentFrames({ ...draft,
      voiceUrl: audio ? audio.attachmentUrl : voice === null ? undefined : previous?.voiceUrl,
      voiceAttachmentId: audio ? audio.id : voice === null ? undefined : previous?.voiceAttachmentId,
    }, savedFrames);
    actions.commit(saved, uploaded);
    const kept = new Set(commentAttachmentIds(saved));
    return { saved, obsoleteIds: previous ? commentAttachmentIds(previous).filter(id => !kept.has(id)) : [] };
  } catch (error) {
    // If cleanup fails the draft's upload owner retains the orphan for a retry.
    await actions.rollback(uploaded).catch(() => undefined);
    throw error;
  }
}

/** Preserve other frames if a caller still uses the single-frame save contract. */
export function singleFrameEdit(file: File, draft: VideoCommentDraft, previous?: VideoComment): VideoFrameDraft[] {
  const existing = previous ? videoCommentFrames(previous) : [];
  const match = existing.find(frame => frame.time === draft.time) || existing[0];
  const changed = { id: match?.id || `${draft.id}:frame`, time: draft.time, frame: draft.frame, file };
  return match ? existing.map(frame => frame.id === match.id ? changed : { ...frame, existing: frame }) : [changed];
}

export function unreferencedCommentAttachments(comment: VideoComment, remaining: VideoComment[], protectedIds: number[] = []): number[] {
  const referenced = new Set([...remaining.flatMap(commentAttachmentIds), ...protectedIds]);
  return commentAttachmentIds(comment).filter(id => !referenced.has(id));
}
